import {
  useActiveWorkspacesLazyQuery,
  useWorkspaceLoginMutation,
  WorkspaceStatusEnum,
} from '@/types/generated/graphql';
import { useCallback, useState } from 'react';

export function useWorkSpaceSecret() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [getActiveWorkspaces, { loading: getActiveWorkspaceLoading }] = useActiveWorkspacesLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [workspaceLogin, { loading: workspaceLoading, data }] = useWorkspaceLoginMutation();

  const getWorkspaceSecret = useCallback(async () => {
    setLoading(true);
    try {
      const activeWsSecretRes = await getActiveWorkspaces();
      const wsList = activeWsSecretRes?.data?.activeWorkspaces?.edges ?? [];
      if (!wsList.length) throw new Error('No active workspaces found.');

      const findCurWs = (ws: (typeof wsList)[number]) => ws?.isCurrent;
      const firstWsId = wsList?.[0]?.id as number;

      let currentWs = wsList?.find(findCurWs);

      // when current workspace is in pending status and another workspace is available
      // then use another workspace to login
      if (
        [WorkspaceStatusEnum.pending, WorkspaceStatusEnum.payment_failed].includes(
          currentWs?.status as WorkspaceStatusEnum
        ) &&
        wsList?.length > 1
      ) {
        currentWs = wsList?.find(
          (workspace) =>
            [WorkspaceStatusEnum.approved, WorkspaceStatusEnum.created].includes(
              workspace?.status as WorkspaceStatusEnum
            ) && workspace?.status !== WorkspaceStatusEnum.payment_failed
        );
      }

      const currentWsId = currentWs?.id ?? firstWsId;
      if (!currentWsId) throw new Error('Failed to determine current workspace ID.');

      const loginWsRes = await workspaceLogin({
        variables: {
          input: {
            workspaceId: currentWsId,
          },
        },
      });
      if (!loginWsRes) throw new Error('Login to workspace failed.');
      return loginWsRes;
    } catch (err) {
      const typedError = err as Error;
      setError(typedError);
      throw typedError;
    } finally {
      setLoading(false);
    }
  }, [getActiveWorkspaces, workspaceLogin]);

  return {
    getWorkspaceSecret,
    workspaceLogin,
    data,
    loading: getActiveWorkspaceLoading || workspaceLoading || loading,
    error,
  };
}
